import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useToast } from "../components/Navbar";
import { useCart } from "../context/CartContext";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";



const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming","District of Columbia",
];

const COUNTRIES = [
  "United States","United Kingdom","Canada","Australia","India","Germany",
  "France","Italy","Spain","Netherlands","Singapore","UAE","Other",
];

function StepIndicator({ step }) {
  const steps = ["Address", "Summary", "Payment"];
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {steps.map((label, i) => {
        const num    = i + 1;
        const active = step === num;
        const done   = step > num;
        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${done ? "bg-emerald-500 text-white" : active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-400"}`}>
                {done ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : num}
              </div>
              <span className={`text-xs font-medium ${active ? "text-stone-900" : done ? "text-emerald-600" : "text-stone-400"}`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-16 h-0.5 mx-2 mb-5 transition-all duration-300 ${done ? "bg-emerald-400" : "bg-stone-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function AddressStep({ onNext, selectedAddress, setSelectedAddress }) {
  const { token } = useAuth();
  const { addToast } = useToast();
  const [addresses,  setAddresses]  = useState([]);
  const [showForm,   setShowForm]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", address_line1: "", address_line2: "",
    city: "", county: "", state: "", zip: "", country: "United States", is_default: false,
  });

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
  useEffect(() => {
    fetch(`${API}/api/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAddresses(data);
          if (data.length === 0) setShowForm(true);
          else {
            const def = data.find(a => a.is_default) || data[0];
            setSelectedAddress(def);
          }
        }
      });
  }, [token, setSelectedAddress]);

  const inp = "w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition";

  async function saveAddress() {
    if (!form.full_name || !form.phone || !form.address_line1 || !form.city || !form.state || !form.zip)
      return addToast("Please fill all required fields", "error");
    setSaving(true);
    try {
      const res  = await fetch(`${API}/api/addresses`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAddresses(prev => [data, ...prev]);
      setSelectedAddress(data);
      setShowForm(false);
      setForm({ full_name: "", phone: "", address_line1: "", address_line2: "", city: "", county: "", state: "", zip: "", country: "United States", is_default: false });
      addToast("Address saved!");
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h2 className="serif text-2xl font-bold text-stone-900 mb-6">Delivery Address</h2>
      {addresses.length > 0 && (
        <div className="space-y-3 mb-6">
          {addresses.map(addr => (
            <div key={addr.id} onClick={() => setSelectedAddress(addr)}
              className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${selectedAddress?.id === addr.id ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-400"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${selectedAddress?.id === addr.id ? "border-stone-900 bg-stone-900" : "border-stone-300"}`}>
                    {selectedAddress?.id === addr.id && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900 text-sm">{addr.full_name}</p>
                    <p className="text-stone-500 text-xs mt-0.5">{addr.phone}</p>
                    <p className="text-stone-600 text-sm mt-1">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ""}</p>
                    <p className="text-stone-600 text-sm">{addr.city}{addr.county ? `, ${addr.county}` : ""}, {addr.state} {addr.zip}</p>
                    <p className="text-stone-500 text-xs">{addr.country}</p>
                  </div>
                </div>
                {addr.is_default === 1 && (
                  <span className="text-[10px] font-bold px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full flex-shrink-0">Default</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {!showForm && (
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 text-sm font-semibold text-rose-500 hover:text-rose-600 transition-colors mb-6">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add new address
        </button>
      )}
      {showForm && (
        <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200 mb-6 space-y-4">
          <h3 className="font-semibold text-stone-900 text-sm">New Address</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">Full Name *</label>
              <input className={inp} placeholder="John Smith" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">Phone *</label>
              <input className={inp} placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">Address Line 1 *</label>
            <input className={inp} placeholder="Street address, P.O. box" value={form.address_line1} onChange={e => setForm(f => ({ ...f, address_line1: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">Address Line 2 <span className="text-stone-400 normal-case font-normal">(optional)</span></label>
            <input className={inp} placeholder="Apt, suite, unit, building, floor" value={form.address_line2} onChange={e => setForm(f => ({ ...f, address_line2: e.target.value }))} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">City *</label>
              <input className={inp} placeholder="New York" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">County <span className="text-stone-400 normal-case font-normal">(optional)</span></label>
              <input className={inp} placeholder="Manhattan" value={form.county} onChange={e => setForm(f => ({ ...f, county: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">ZIP Code *</label>
              <input className={inp} placeholder="10001" value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">State *</label>
              <select className={inp} value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}>
                <option value="">Select state...</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5 block">Country *</label>
            <select className={inp} value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} className="w-4 h-4 rounded" />
            <span className="text-sm text-stone-600">Set as default address</span>
          </label>
          <div className="flex gap-3">
            <button onClick={saveAddress} disabled={saving} className="flex-1 py-3 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-rose-500 transition-all duration-300 disabled:opacity-60">
              {saving ? "Saving..." : "Save Address"}
            </button>
            {addresses.length > 0 && (
              <button onClick={() => setShowForm(false)} className="px-5 py-3 rounded-xl border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-50 transition-colors">
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
      <button onClick={onNext} disabled={!selectedAddress}
        className="w-full py-4 rounded-2xl bg-stone-900 text-white font-semibold hover:bg-rose-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
        Continue to Order Summary →
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   STEP 2 — ORDER SUMMARY + REWARD POINTS
═══════════════════════════════════════════════════ */
function SummaryStep({ onNext, onBack, selectedAddress }) {
  const { items, totalPrice } = useCart();
  const { token } = useAuth();
  const { addToast } = useToast();

  const [delivery, setDelivery] = useState(null); // null = still loading

useEffect(() => {
  // NOTE: falls back to 300g/item until products carry a real weight_grams field
  const weight_grams = items.reduce((sum, i) => {
    const qty = i.qty || i.quantity || 1;
    const w   = i.weight_grams || 300;
    return sum + w * qty;
  }, 0);

  fetch(`${API}/api/admin/shipping/calculate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ order_total: totalPrice, weight_grams }),
  })
    .then(r => r.json())
    .then(data => setDelivery(data.charge ?? 9.99))
    .catch(() => setDelivery(9.99)); // safe fallback if the endpoint is unreachable
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [totalPrice]);

  // ── Reward points state ──
  const [rewardBalance,    setRewardBalance]    = useState(0);
  const [usePoints,        setUsePoints]        = useState(false);
  const [pointsToUse,      setPointsToUse]      = useState(0);
  const [rewardDiscount,   setRewardDiscount]   = useState(0);
  const [rewardValidating, setRewardValidating] = useState(false);

  // ── Discount code state ──
  const [discountCode,     setDiscountCode]     = useState("");
  const [discountApplied,  setDiscountApplied]  = useState(null); // { code, discount_amount, message }
  const [discountLoading,  setDiscountLoading]  = useState(false);

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/rewards`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setRewardBalance(data.balance || 0))
      .catch(() => {});
  }, [token]);

  async function handleTogglePoints(checked) {
    setUsePoints(checked);
    if (!checked) { setRewardDiscount(0); setPointsToUse(0); return; }
    if (rewardBalance <= 0) {
      addToast("You have no reward points to use", "error");
      setUsePoints(false);
      return;
    }
    setRewardValidating(true);
    try {
      const subtotal = totalPrice + delivery;
      const res = await fetch(`${API}/api/rewards/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ points_to_use: rewardBalance, order_total: subtotal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setPointsToUse(data.points_to_use);
      setRewardDiscount(data.discount_amount);
      addToast(`${data.points_to_use} points applied — $${data.discount_amount} off! 🎉`);
    } catch (err) {
      addToast(err.message || "Could not apply points", "error");
      setUsePoints(false);
    } finally {
      setRewardValidating(false);
    }
  }

  async function applyDiscountCode() {
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    try {
      const subtotal = totalPrice + delivery;
      const res = await fetch(`${API}/api/admin/discounts/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: discountCode.trim(), order_total: subtotal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setDiscountApplied(data);
      addToast(data.message || "Discount applied! 🎉");
    } catch (err) {
      addToast(err.message || "Invalid discount code", "error");
      setDiscountApplied(null);
    } finally {
      setDiscountLoading(false);
    }
  }

  function removeDiscountCode() {
    setDiscountApplied(null);
    setDiscountCode("");
  }

  const subtotal       = totalPrice;
  const discountAmount = discountApplied?.discount_amount || 0;
  const total          = delivery == null
    ? null
    : parseFloat((subtotal + delivery - rewardDiscount - discountAmount).toFixed(2));
  const pointsEarned   = total == null ? 0 : Math.floor(total);

  const inp = "w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition";

  return (
    <div>
      <h2 className="serif text-2xl font-bold text-stone-900 mb-6">Order Summary</h2>

      {/* Items */}
      <div className="space-y-4 mb-6">
        {items.map(item => {
          const qty    = item.qty || item.quantity || 1;
          const price  = parseFloat(item.effective_price ?? item.sale_price ?? item.price ?? 0);
          const imgSrc = item.img || (Array.isArray(item.images) ? item.images[0] : "") || "";
          const key    = item.product_id || item.id;
          return (
            <div key={key} className="flex gap-4 items-start p-4 bg-stone-50 rounded-2xl">
              {imgSrc && <img src={imgSrc} alt={item.name} className="w-16 h-16 object-cover rounded-xl flex-shrink-0 bg-stone-200" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-stone-900 text-sm truncate">{item.name}</p>
                <div className="flex gap-2 mt-0.5">
                  {item.selectedSize  && <span className="text-xs text-stone-500">Size: {item.selectedSize}</span>}
                  {item.selectedColor && <span className="text-xs text-stone-500 capitalize">Color: {item.selectedColor}</span>}
                </div>
                <p className="text-xs text-stone-400 mt-0.5">Qty: {qty}</p>
              </div>
              <p className="font-bold text-stone-900 text-sm flex-shrink-0">${(price * qty).toLocaleString()}</p>
            </div>
          );
        })}
      </div>

      {/* Delivery address */}
      <div className="bg-stone-50 rounded-2xl p-4 mb-6 border border-stone-200">
        <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-2">Delivering To</p>
        <p className="font-semibold text-stone-900 text-sm">{selectedAddress?.full_name}</p>
        <p className="text-stone-500 text-xs">{selectedAddress?.phone}</p>
        <p className="text-stone-600 text-sm mt-1">{selectedAddress?.address_line1}{selectedAddress?.address_line2 ? `, ${selectedAddress.address_line2}` : ""}</p>
        <p className="text-stone-600 text-sm">{selectedAddress?.city}{selectedAddress?.county ? `, ${selectedAddress.county}` : ""}, {selectedAddress?.state} {selectedAddress?.zip}</p>
        <p className="text-stone-500 text-xs">{selectedAddress?.country}</p>
      </div>

      {/* ── Discount Code Box ── */}
      <div className="rounded-2xl p-4 mb-6 border-2 border-stone-200 bg-white">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-3">Discount Code</p>
        {discountApplied ? (
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-emerald-600 font-bold text-sm">✓ {discountApplied.code}</span>
              <span className="text-emerald-500 text-xs">{discountApplied.message}</span>
            </div>
            <button onClick={removeDiscountCode} className="text-stone-400 hover:text-red-500 transition-colors text-xs font-semibold ml-2">
              Remove
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              className={inp + " flex-1"}
              placeholder="Enter code (e.g. SAVE10)"
              value={discountCode}
              onChange={e => setDiscountCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && applyDiscountCode()}
            />
            <button
              onClick={applyDiscountCode}
              disabled={discountLoading || !discountCode.trim()}
              className="px-5 py-3 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-rose-500 transition-all duration-300 disabled:opacity-50 flex-shrink-0"
            >
              {discountLoading ? "..." : "Apply"}
            </button>
          </div>
        )}
      </div>

      {/* ── Reward Points Box ── */}
      {rewardBalance > 0 && (
        <div className={`rounded-2xl p-4 pr-5 mb-6 border-2 transition-all duration-300 overflow-visible ${usePoints ? "border-amber-400 bg-amber-50" : "border-stone-200 bg-white"}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-lg flex-shrink-0">🪙</div>
              <div className="min-w-0">
                <p className="font-bold text-stone-900 text-sm">Reward Points</p>
                <p className="text-xs text-stone-500 truncate">You have <span className="font-bold text-amber-600">{rewardBalance} pts</span> · 100 pts = $1 off</p>
              </div>
            </div>
            <button
              onClick={() => handleTogglePoints(!usePoints)}
              disabled={rewardValidating}
              className={`relative flex-shrink-0 w-12 h-7 rounded-full transition-colors duration-300 focus:outline-none disabled:opacity-50 ${usePoints ? "bg-emerald-500" : "bg-stone-900"}`}>
              <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${usePoints ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>
          {usePoints && rewardDiscount > 0 && (
            <div className="mt-3 flex items-center gap-2 bg-amber-100 rounded-xl px-3 py-2">
              <span className="text-amber-700 text-xs font-bold">✓ {pointsToUse} points applied</span>
              <span className="text-amber-600 text-xs ml-auto font-bold">-${rewardDiscount.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {/* Price breakdown */}
      <div className="bg-white rounded-2xl p-5 border border-stone-200 space-y-3 mb-6">
        <div className="flex justify-between text-sm text-stone-600">
          <span>Subtotal ({items.reduce((s, i) => s + (i.qty || 1), 0)} items)</span>
          <span className="font-semibold text-stone-900">${subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm text-stone-600">
          <span>Delivery</span>
          <span className={`font-semibold ${delivery === 0 ? "text-emerald-600" : "text-stone-900"}`}>
            {delivery == null ? "…" : delivery === 0 ? "FREE" : `$${delivery.toFixed(2)}`}
          </span>
        </div>
        {delivery === 0 && (
          <p className="text-xs text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-xl">🎉 You qualify for free delivery!</p>
        )}
        {discountAmount > 0 && (
          <div className="flex justify-between text-sm text-emerald-600">
            <span>🏷️ Discount ({discountApplied?.code})</span>
            <span className="font-semibold">-${discountAmount.toFixed(2)}</span>
          </div>
        )}
        {rewardDiscount > 0 && (
          <div className="flex justify-between text-sm text-amber-600">
            <span>🪙 Reward Points</span>
            <span className="font-semibold">-${rewardDiscount.toFixed(2)}</span>
          </div>
        )}
        <div className="border-t border-stone-100 pt-3 flex justify-between">
          <span className="font-bold text-stone-900">Total</span>
          <span className="serif text-xl font-bold text-stone-900">{total == null ? "…" : `$${total.toFixed(2)}`}</span>
        </div>
        <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 mt-1">
          <span className="text-lg">🪙</span>
          <span className="text-xs text-stone-500">You'll earn <span className="font-bold text-stone-700">{pointsEarned} reward points</span> for this order</span>
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={onBack} className="px-6 py-4 rounded-2xl border border-stone-200 text-stone-600 font-semibold hover:bg-stone-50 transition-colors text-sm">
          ← Back
        </button>
        <button
          onClick={() => onNext(total, delivery, pointsToUse, rewardDiscount, discountApplied?.code, discountAmount, subtotal)}
          disabled={total == null}
          className="flex-1 py-4 rounded-2xl bg-stone-900 text-white font-semibold hover:bg-rose-500 transition-all duration-300 disabled:opacity-50">
          Continue to Payment →
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   STEP 3 — STRIPE PAYMENT FORM
   (clientSecret is now created BEFORE this mounts,
   by PaymentStep below — needed for Apple Pay / wallets
   to be detected and displayed by PaymentElement)
═══════════════════════════════════════════════════ */
function PaymentForm({ total, clientSecret, rewardPointsUsed, rewardDiscount, orderPayload, onSuccess }) {
  const stripe     = useStripe();
  const elements   = useElements();
  const { token } = useAuth();
  const { clearCart } = useCart();
  const { addToast } = useToast();
  const [paying,   setPaying]   = useState(false);
  const [error,    setError]    = useState("");

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  async function handlePay(e) {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setError("");
    setPaying(true);

    try {
      // Stripe requires elements.submit() to validate the payment form,
      // called before any other work — including confirmPayment().
      const { error: submitError } = await elements.submit();
      if (submitError) throw new Error(submitError.message);

      // Save order details in case this payment method redirects the
      // customer away (e.g. 3D Secure) — PaymentReturn.jsx reads this back.
      localStorage.setItem("sf_pending_order", JSON.stringify(orderPayload));

      // ── The PaymentIntent already exists (created by PaymentStep before
      //    this form even mounted) — just confirm it. ──
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/payment-return`,
        },
      });

      if (stripeError) throw new Error(stripeError.message);

      // ── If payment succeeded immediately (no redirect needed), create the real order now ──
      if (paymentIntent.status === 'succeeded') {
        const orderRes = await fetch(`${API}/api/orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            ...orderPayload,
            stripe_payment_id: paymentIntent.id,
          }),
        });

        const orderResult = await orderRes.json().catch(() => ({}));
        if (!orderRes.ok) {
          throw new Error(orderResult.message || "Payment succeeded but order creation failed. Please contact support.");
        }

        localStorage.removeItem("sf_pending_order");
        clearCart();
        addToast("Payment successful! Your order is being processed.");
        if (onSuccess) onSuccess(orderResult.id);
      } else {
        addToast("Redirecting to complete your payment...");
      }

    } catch (err) {
      setError(err.message);
      addToast(err.message, "error");
    } finally {
      setPaying(false);
    }
  }

  return (
    <form onSubmit={handlePay}>
      <h2 className="serif text-2xl font-bold text-stone-900 mb-6">Payment</h2>
      <div className="bg-stone-50 rounded-2xl p-4 mb-6 flex items-center justify-between border border-stone-200">
        <span className="text-sm text-stone-600 font-medium">Amount to Pay</span>
        <span className="serif text-2xl font-bold text-stone-900">${total.toFixed(2)}</span>
      </div>
      {rewardDiscount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-5 flex items-center gap-2">
          <span className="text-lg">🪙</span>
          <span className="text-xs text-amber-700 font-semibold">{rewardPointsUsed} reward points applied — ${rewardDiscount.toFixed(2)} discount included</span>
        </div>
      )}
      <div className="mb-6">
        <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3 block">Payment Details</label>
        <div className="p-4 rounded-2xl border-2 border-stone-200 focus-within:border-stone-900 transition-colors">
          <PaymentElement
            options={{
              defaultValues: {
                billingDetails: {
                  address: { country: "US" },
                },
              },
            }}
          />
        </div>
        <p className="text-xs text-stone-400 mt-2">🔒 Your payment info is encrypted and secure via Stripe.</p>
      </div>
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>}
      <div className="flex items-center justify-center gap-6 mb-6">
        {["SSL Secure", "Stripe Verified", "256-bit Encrypted"].map(badge => (
          <div key={badge} className="flex items-center gap-1.5 text-xs text-stone-400">
            <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            {badge}
          </div>
        ))}
      </div>
      <button type="submit" disabled={!stripe || !clientSecret || paying}
        className="w-full py-4 rounded-2xl bg-rose-500 text-white font-bold text-base hover:bg-rose-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-rose-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0">
        {paying ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Processing...
          </span>
        ) : `Pay $${total.toFixed(2)}`}
      </button>

      <p className="text-center text-xs text-stone-400 mt-3">
        🔒 Secure checkout powered by Stripe.{" "}
        By placing your order you agree to our{" "}
        <Link to="/returns" className="underline hover:text-rose-500 transition-colors">Returns & Refund Policy</Link>.
      </p>
    </form>
  );
}

/* ═══════════════════════════════════════════════════
   PaymentStep — creates the PaymentIntent BEFORE
   rendering <Elements>, and passes clientSecret into
   Elements' `options`. This is required for wallet
   payment methods (Apple Pay, Google Pay) to be
   detected and shown by <PaymentElement /> — without
   it, Stripe has nothing to evaluate wallet eligibility
   against and silently falls back to card-only.
═══════════════════════════════════════════════════ */
function PaymentStep({ total, deliveryCharge, selectedAddress, rewardPointsUsed, rewardDiscount, discountCode, discountAmount, subtotal, onBack, onSuccess }) {
  const { token } = useAuth();
  const { items } = useCart();
  const { addToast } = useToast();
  const [clientSecret, setClientSecret] = useState(null);
  const [loadError,    setLoadError]    = useState("");

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  // Built once — reused both to create the PaymentIntent and, after payment
  // succeeds, to actually create the order via POST /api/orders.
  const orderPayload = {
    address_id: selectedAddress.id,
    items: items.map(item => ({
      id: item.product_id || item.id,
      qty: item.qty || item.quantity || 1,
      image: item.img || (Array.isArray(item.images) ? item.images[0] : "") || null,
      price: item.effective_price ?? item.sale_price ?? item.price,
      name: item.name,
      selectedSize: item.selectedSize || null,
      selectedColor: item.selectedColor || null,
    })),
    subtotal: subtotal,
    delivery_charge: deliveryCharge,
    total: total,
    reward_points_used: rewardPointsUsed,
    reward_discount: rewardDiscount,
    discount_code: discountCode || null,
    discount_amount: discountAmount || 0,
  };

  useEffect(() => {
    let cancelled = false;

    fetch(`${API}/api/payment/create-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        amount: total,
        currency: "usd",
        ...orderPayload,
      }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.message || "Could not start payment.");
        if (!cancelled) setClientSecret(data.clientSecret);
      })
      .catch(err => {
        if (!cancelled) {
          setLoadError(err.message);
          addToast(err.message, "error");
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {loadError ? (
        <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 mb-6">
          {loadError}
        </div>
      ) : !clientSecret ? (
        <div className="py-16 text-center">
          <svg className="w-6 h-6 animate-spin mx-auto mb-3 text-stone-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-sm text-stone-400">Loading payment options...</p>
        </div>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentForm
            total={total}
            clientSecret={clientSecret}
            rewardPointsUsed={rewardPointsUsed}
            rewardDiscount={rewardDiscount}
            orderPayload={orderPayload}
            onSuccess={onSuccess}
          />
        </Elements>
      )}
      <button onClick={onBack} className="mt-4 text-sm text-stone-500 hover:text-rose-500 transition-colors">← Back to Summary</button>
    </div>
  );
}

export { stripePromise };

export default function CheckoutPage() {
  const { items }    = useCart();
  const { isLoggedIn } = useAuth();
  const navigate     = useNavigate();

  const [step,             setStep]             = useState(1);
  const [selectedAddress,  setSelectedAddress]  = useState(null);
  const [orderTotal,       setOrderTotal]       = useState(0);
  const [deliveryCharge,   setDeliveryCharge]   = useState(0);
  const [rewardPointsUsed, setRewardPointsUsed] = useState(0);
  const [rewardDiscount,   setRewardDiscount]   = useState(0);
  const [discountCode,   setDiscountCode]   = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [orderSubtotal, setOrderSubtotal] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) { navigate("/"); return; }
    if (items.length === 0) navigate("/shop");
  }, [isLoggedIn, items, navigate]);

  function handleSummaryNext(total, delivery, pointsUsed, rewardDisc, dCode, dAmount, subtotal) {
    setOrderTotal(total);
    setDeliveryCharge(delivery);
    setRewardPointsUsed(pointsUsed || 0);
    setRewardDiscount(rewardDisc || 0);
    setDiscountCode(dCode || "");
    setDiscountAmount(dAmount || 0);
    setOrderSubtotal(subtotal || 0);
    setStep(3);
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20 pb-16">
      <div className="bg-white border-b border-stone-100 px-4 py-4 mb-8">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/shop" className="flex items-center gap-2 text-stone-600 hover:text-rose-500 transition-colors text-sm font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Continue Shopping
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-400 to-amber-500 flex items-center justify-center">
              <span className="text-white text-[10px] font-bold">SF</span>
            </div>
            <span className="serif text-lg font-bold text-stone-900">Checkout</span>
          </div>
          <div className="w-24" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4">
        <StepIndicator step={step} />
        <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6 md:p-8 overflow-visible">
          {step === 1 && (
            <AddressStep
              onNext={() => setStep(2)}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
            />
          )}
          {step === 2 && (
            <SummaryStep
              onNext={handleSummaryNext}
              onBack={() => setStep(1)}
              selectedAddress={selectedAddress}
            />
          )}
          {step === 3 && (
            <PaymentStep
              total={orderTotal}
              deliveryCharge={deliveryCharge}
              subtotal={orderSubtotal}
              selectedAddress={selectedAddress}
              rewardPointsUsed={rewardPointsUsed}
              rewardDiscount={rewardDiscount}
              discountCode={discountCode}
              discountAmount={discountAmount}
              onBack={() => setStep(2)}
              onSuccess={(orderId) => navigate(`/order-confirmation/${orderId}`)}
            />
          )}
        </div>
      </div>
    </div>
  );
}